import { useQuery } from "@tanstack/vue-query";
import { computed, type Ref } from "vue";
import { getAllOpenItemsForClient } from "../../services/contador/open-items.service";
import { useOrganization } from "../useOrganization";
import { getUserEmail } from "../../config/firebase.ts";

export function useOpenItemsData(showResolved: Ref<boolean> = computed(() => false)) {
    const {
        organization,
        loadingOrganization: isLoadingOrganization,
        organizationError,
        isOrganizationError,
    } = useOrganization();

    const clientId = computed(() => organization.value?.id ?? null);

    const {
        data: openItems,
        isLoading: isLoadingOpenItems,
        error: openItemsError,
        isError: isOpenItemsError,
        refetch,
    } = useQuery({
        queryKey: computed(() => ["client-open-items", clientId.value, showResolved.value]),
        queryFn: () => {
            if (!clientId.value) {
                throw new Error("Client ID is required");
            }
            const propertiesIDs = getCurrentUserLinkedPropertiesIds(organization.value);
            return getAllOpenItemsForClient(clientId.value, propertiesIDs, showResolved.value);
        },
        enabled: computed(() => !!clientId.value),
    });

    const stats = computed(() => {
        if (!openItems.value) return null;

        const now = new Date();
        const openCount = openItems.value.filter((item) => item.status === "open").length;
        const pastDueCount = openItems.value.filter((item) => {
            if (item.status !== "open" || !item.due_date) return false;
            return new Date(item.due_date) < now;
        }).length;

        const newMessagesCount = openItems.value.filter((item) => {
            if (item.status !== "open") return false;
            if (!item.comments || item.comments.length === 0) return false;
            const lastComment = item.comments[item.comments.length - 1];
            return lastComment.author.endsWith("@proper.ai");
        }).length;

        return {
            open_count: openCount,
            past_due_count: pastDueCount,
            new_messages_count: newMessagesCount,
        };
    });

    return {
        openItems,
        stats,
        isLoading: computed(() => isLoadingOrganization.value || isLoadingOpenItems.value),
        error: computed<Error | null>(() => organizationError.value ?? openItemsError.value),
        isError: computed<boolean>(() => isOrganizationError.value || isOpenItemsError.value),
        refetch,
    };
}

function getCurrentUserLinkedPropertiesIds(organization: any): string[] {
    const userEmail = getUserEmail();
    const contact = organization.contacts.find(
        (c: any) => c.email.toLowerCase() === userEmail?.toLowerCase(),
    );
    return contact?.properties_ids ?? [];
}
